import React, { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { InventoryItem } from 'common/modules/inventory';
import { useTranslation } from 'services/localization';
import { convertSeconds, formatTime } from 'common/utils/dateUtils';

interface Props {
	item: InventoryItem;
}
const ItemActivity = ({ item }: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const { usageCount, usageSeconds } = item;
	const [usageTimeUnit, setUsageTimeUnit] = useState<'hours' | 'days'>('hours');

	const toggleUsageTimeUnit = () => {
		setUsageTimeUnit((prev) => (prev === 'hours' ? 'days' : 'hours'));
	};

	return (
		<Box className={classes.activityContainer}>
			<Stack direction="row" justifyContent="space-evenly">
				<DataItem
					title={t('inventory.columns.usageCount', 'Times used')}
					value={usageCount.toString()}
				/>
				{usageTimeUnit === 'hours' ? (
					<DataItem
						title={t('inventory.columns.usageHours', 'Hours used')}
						value={formatTime(convertSeconds(usageSeconds, 'hours'), { precision: 1, suffix: 'h' })}
						onClick={toggleUsageTimeUnit}
					/>
				) : usageTimeUnit === 'days' ? (
					<DataItem
						title={t('inventory.columns.usageDays', 'Days used')}
						value={formatTime(convertSeconds(usageSeconds, 'days'), { precision: 1, suffix: 'd' })}
						onClick={toggleUsageTimeUnit}
					/>
				) : null}
			</Stack>
		</Box>
	);
};

const DataItem = ({
	title,
	value,
	onClick,
}: {
	title: string;
	value: string;
	onClick?: () => void;
}) => {
	return (
		<Box
			minWidth={120}
			textAlign="center"
			sx={{
				cursor: !!onClick ? 'pointer' : 'auto',
				':hover p': { textDecoration: !!onClick ? 'underline' : 'initial' },
			}}
			onClick={onClick}
		>
			<Typography
				variant="body2"
				sx={{
					fontWeight: 400,
					mb: 1,
				}}
			>
				{title}
			</Typography>
			<Typography variant="h5">{value}</Typography>
		</Box>
	);
};

const useStyles = makeStyles((theme) => ({
	activityContainer: {
		backgroundColor: theme.palette.background.secondary,
		border: `1px solid ${theme.palette.border.paper}`,
		borderRadius: 4,
		padding: theme.spacing(2),
	},
}));

export default ItemActivity;
